<template>
  <v-row>

    <!-- ── Painel principal ────────────────────────────────────────────────── -->
    <v-col cols="12">
      <div id="dash-container" ref="dashCard" class="dash-container" :class="{ 'dash-container--fullscreen': isFullscreen }">
        <v-progress-linear indeterminate absolute :active="carregando" color="primary" style="top:0;z-index:10;" />

        <!-- Cabeçalho flutuante -->
        <div class="dash-header">
          <div class="dash-header-icon">
            <v-icon size="16" color="white">mdi-chart-areaspline</v-icon>
          </div>
          <div>
            <div class="dash-header-title">Impacto Fiscal por Filial</div>
            <div class="dash-header-sub">Economia gerada pela inclusão de E-books nas vendas</div>
          </div>
          <v-spacer />

          <!-- Toggle filtro -->
          <v-btn
              icon small
              class="dash-filter-btn mr-1"
              :class="{ 'dash-filter-btn--active': mostrarFiltro }"
              @click="mostrarFiltro = !mostrarFiltro"
          >
            <v-icon size="15">{{ mostrarFiltro ? 'mdi-filter' : 'mdi-filter-outline' }}</v-icon>
          </v-btn>

          <!-- Tela cheia -->
          <v-btn
              icon small
              class="dash-filter-btn"
              :class="{ 'dash-filter-btn--active': isFullscreen }"
              @click="toggleFullscreen"
          >
            <v-icon size="15">{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
          </v-btn>
        </div>

        <!-- ── Filtros ───────────────────────────────────────────────────── -->
        <div class="dash-filter-wrap" :class="{ 'dash-filter-wrap--hidden': !mostrarFiltro }">
          <div class="dash-filter-clip">
            <div class="dash-filter">
              <v-form @submit.prevent="buscar">
                <v-row align="center" no-gutters>
                  <v-col cols="12" sm class="px-1 mb-2 mb-sm-0">
                    <SeletorPeriodo
                        v-model="busca.periodo"
                        label="Período *"
                        :disabled="carregando"
                        :shortcuts="['periods', 'weeks', 'months']"
                        :attach="isFullscreen ? '#dash-container' : false"
                    />
                  </v-col>
                  <v-col cols="12" sm="auto" class="px-1">
                    <v-btn
                        class="primary elevation-0"
                        :block="$vuetify.breakpoint.xsOnly"
                        :loading="carregando"
                        :disabled="!busca.periodo || !busca.periodo.inicio"
                        style="border-radius: 8px; height: 40px;"
                        @click="buscar"
                    >
                      <v-icon small left>mdi-magnify</v-icon>
                      Buscar
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </div>
          </div>
        </div>

        <!-- ── Alerta informativo ────────────────────────────────────────── -->
        <transition name="dash-alert">
          <div v-if="alertaFiscal" class="dash-alert-wrap">
            <div class="dash-alert-ico-wrap">
              <v-icon size="22" color="#0477a1">mdi-information-outline</v-icon>
            </div>
            <div class="dash-alert-txt">
              <div class="dash-alert-title">Valores Estimados — Atenção</div>
              <div class="dash-alert-body">
                Os valores exibidos como <strong>Real</strong> e <strong>Projeção</strong> são estimativas calculadas
                com base nas vendas do período selecionado. No <strong>fechamento fiscal do mês</strong>, os impostos
                definitivos apurados pela contabilidade podem apresentar <strong>variações</strong> em relação
                aos valores aqui apresentados.
              </div>
            </div>
            <button class="dash-alert-close" title="Fechar" @click="alertaFiscal = false">
              <v-icon size="16" color="#0477a1">mdi-close</v-icon>
            </button>
          </div>
        </transition>

        <!-- ── KPIs ──────────────────────────────────────────────────────── -->
        <div v-if="carregando" class="dash-kpi-area">
          <div v-for="n in 3" :key="`ksk-${n}`" class="kpi-card">
            <div class="kpi-body">
              <div class="kpi-head" style="margin-bottom:10px;">
                <div class="dsk dsk-circle" style="width:24px;height:24px;flex-shrink:0;border-radius:6px;"></div>
                <div class="kpi-head-info">
                  <div class="dsk dsk-text" style="width:70px;margin-bottom:7px;"></div>
                  <div class="dsk dsk-text" style="width:120px;height:16px;"></div>
                </div>
              </div>
              <div class="kpi-sep"></div>
              <div class="kpi-row" style="margin-top:7px;">
                <div class="dsk dsk-text" style="width:110px;"></div>
                <div class="dsk dsk-text" style="width:38px;"></div>
              </div>
              <div class="kpi-row">
                <div class="dsk dsk-text" style="width:80px;"></div>
                <div class="dsk dsk-text" style="width:28px;"></div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="totais" class="dash-kpi-area">

          <!-- Saving Fiscal -->
          <div class="kpi-card">
            <div class="kpi-body">
              <div class="kpi-head">
                <div class="kpi-ico kpi-ico--primary"><v-icon size="14" color="#023449">mdi-lightning-bolt</v-icon></div>
                <div class="kpi-head-info">
                  <div class="kpi-title">Saving Fiscal</div>
                  <div class="kpi-main">{{ economiaImposto(totais) | formataDinheiro }}</div>
                </div>
              </div>
              <div class="kpi-sep"></div>
              <div class="kpi-row">
                <span class="kpi-row-label"><v-icon size="12" color="#023449">mdi-trending-down</v-icon> Redução de impostos</span>
                <span class="kpi-row-val kpi-val--green">{{ reducaoMedia }}%</span>
              </div>
              <div class="kpi-row">
                <span class="kpi-row-label"><v-icon size="12" color="#023449">mdi-store-outline</v-icon> Filiais</span>
                <span class="kpi-row-val kpi-val--primary">{{ dados.length }}</span>
              </div>
              <div class="kpi-filiais-detail">
                <span v-if="contagemFiliais.real" class="kpi-filial-tag kpi-filial-tag--real">{{ contagemFiliais.real }} real</span>
                <span v-if="contagemFiliais.proj" class="kpi-filial-tag kpi-filial-tag--proj">{{ contagemFiliais.proj }} projeção</span>
              </div>
            </div>
          </div>

          <!-- Vendas -->
          <div class="kpi-card kpi-card--fat">
            <div class="kpi-body">
              <div class="kpi-head">
                <div class="kpi-ico kpi-ico--fat"><v-icon size="14" color="#1565c0">mdi-store-outline</v-icon></div>
                <div class="kpi-head-info">
                  <div class="kpi-title">Total de Vendas</div>
                  <div class="kpi-main">{{ totais.vlr_venda | formataDinheiro }}</div>
                </div>
              </div>
              <div class="kpi-sep"></div>
              <div class="kpi-row">
                <span class="kpi-row-label"><v-icon size="12" color="#1565c0">mdi-book-education-outline</v-icon> Qtd e-books</span>
                <span class="kpi-row-val kpi-val--primary">{{ totais.qtd_ebooks }}</span>
              </div>
              <div class="kpi-row">
                <span class="kpi-row-label"><v-icon size="12" color="#1565c0">mdi-cash</v-icon> Valor e-books</span>
                <span class="kpi-row-val kpi-val--primary">{{ totais.vlr_ebooks | formataDinheiro }}</span>
              </div>
              <div class="kpi-bar-wrap" style="margin-top:6px">
                <div class="kpi-bar-track"><div class="kpi-bar kpi-bar--ebook" :style="{ width: (totais.vlr_ebooks / totais.vlr_venda * 100).toFixed(1) + '%' }"></div></div>
                <div class="kpi-share-sub">
                  <span>participação nas vendas</span>
                  <span class="kpi-val--primary">{{ (totais.vlr_ebooks / totais.vlr_venda * 100).toFixed(1) }}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Impostos -->
          <div class="kpi-card kpi-card--imp">
            <div class="kpi-body">
              <div class="kpi-head">
                <div class="kpi-ico kpi-ico--imp"><v-icon size="14" color="#c62828">mdi-bank-outline</v-icon></div>
                <div class="kpi-head-info">
                  <div class="kpi-title">Impostos — Antes vs Depois</div>
                </div>
                <div class="kpi-reduc-badge">↓ {{ reducaoMedia }}%</div>
              </div>
              <div class="kpi-sep"></div>
              <div class="kpi-row">
                <span class="kpi-row-label"><span class="kpi-dot kpi-dot--sem"></span> Sem e-book</span>
                <span class="kpi-row-val kpi-val--red">{{ totais.vlr_imposto_sem | formataDinheiro }}</span>
              </div>
              <div class="kpi-bar-wrap"><div class="kpi-bar-track"><div class="kpi-bar kpi-bar--sem" style="width:100%"></div></div></div>
              <div class="kpi-row" style="margin-top:4px">
                <span class="kpi-row-label"><span class="kpi-dot kpi-dot--com"></span> Com e-book</span>
                <span class="kpi-row-val kpi-val--green">{{ totais.vlr_imposto_com | formataDinheiro }}</span>
              </div>
              <div class="kpi-bar-wrap"><div class="kpi-bar-track"><div class="kpi-bar kpi-bar--com" :style="{ width: (totais.vlr_imposto_com / totais.vlr_imposto_sem * 100).toFixed(1) + '%' }"></div></div></div>
            </div>
          </div>

        </div>

        <!-- Divisor: Detalhamento -->
        <div class="dash-section-divider">
          <span class="dash-section-divider-line" />
          <span class="dash-section-divider-label">
            <v-icon size="11" color="#94a3b8" class="mr-1">mdi-table-large</v-icon>
            Detalhamento por Filial
          </span>
          <span class="dash-section-divider-line" />
        </div>

        <!-- Card de conteúdo -->
        <v-card elevation="0" outlined class="dash-content-card">

          <!-- Toolbar com chips de grupo -->
          <div v-if="dados.length" class="dash-toolbar">
            <transition name="fade-chip">
              <v-chip
                  v-if="filtroFilial"
                  x-small close
                  color="primary" outlined
                  class="mr-2 dash-filter-chip"
                  @click:close="filtroFilial = null"
              >
                <v-icon x-small left>mdi-filter</v-icon>
                {{ filtroFilial.empresa }}
              </v-chip>
            </transition>

            <div class="grupo-chips">
              <button class="grupo-chip" :class="{ 'grupo-chip--on': grupos.ebooks }" @click="grupos.ebooks = !grupos.ebooks">
                <v-icon size="11" class="mr-1">mdi-book-education-outline</v-icon>E-books
              </button>
              <button class="grupo-chip" :class="{ 'grupo-chip--on': grupos.resultado }" @click="grupos.resultado = !grupos.resultado">
                <v-icon size="11" class="mr-1">mdi-chart-line</v-icon>Resultado
              </button>
              <button class="grupo-chip" :class="{ 'grupo-chip--on': grupos.impostos }" @click="grupos.impostos = !grupos.impostos">
                <v-icon size="11" class="mr-1">mdi-bank-outline</v-icon>Impostos
              </button>
              <div v-if="grupos.impostos" class="d-none d-sm-flex align-center" style="gap:4px;">
                <span class="grupo-sep">|</span>
                <button class="grupo-chip grupo-chip--sub" :class="{ 'grupo-chip--on': verDetalhe }" @click="verDetalhe = !verDetalhe">
                  <v-icon size="10" class="mr-1">mdi-table-column</v-icon>ICMS/PIS/COFINS
                </button>
              </div>
            </div>

            <v-spacer />

            <v-btn icon x-small class="dash-filter-btn" :class="{ 'dash-filter-btn--active': isFullscreen }" @click="toggleFullscreen">
              <v-icon size="15">{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            </v-btn>
          </div>

          <!-- ── Visão: Tabela ─────────────────────────────────────────────── -->
          <div v-if="dados.length || carregando" class="dash-table-wrap d-none d-md-block">
            <table class="dash-table">
              <thead>
              <tr class="dash-thead-g">
                <th class="dash-thg dash-thg--rk dash-th--sortable" :class="{ 'dash-th--sorted': sortKey === 'rk' }" rowspan="2" @click="sortBy('rk')">
                  <div class="thg-inner thg-inner--center">
                    <v-icon size="11" style="margin-right:3px;">mdi-trophy-outline</v-icon>
                    RK
                    <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'rk' }">{{ sortIcon('rk') }}</v-icon>
                  </div>
                </th>
                <th class="dash-thg dash-thg--filial dash-th--sortable" :class="{ 'dash-th--sorted': sortKey === 'empresa' }" rowspan="2" @click="sortBy('empresa')">
                  <div class="thg-inner">
                    <v-icon size="11" style="margin-right:3px;">mdi-store-outline</v-icon>
                    Filial
                    <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'empresa' }">{{ sortIcon('empresa') }}</v-icon>
                  </div>
                </th>
                <th class="dash-thg dash-thg--venda dash-th--sortable" :class="{ 'dash-th--sorted': sortKey === 'vlr_venda' }" rowspan="2" title="Valor real de vendas" @click="sortBy('vlr_venda')">
                  <div class="thg-inner">Venda Total <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'vlr_venda' }">{{ sortIcon('vlr_venda') }}</v-icon></div>
                </th>
                <th v-if="grupos.ebooks" class="dash-thg dash-thg--ebook" colspan="3">E-books</th>
                <th v-if="grupos.resultado" class="dash-thg dash-thg--resultado" colspan="4">Resultado</th>
                <th v-if="grupos.impostos" class="dash-thg dash-thg--sem" :colspan="verDetalhe ? 4 : 1">Impostos s/ E-book</th>
                <th v-if="grupos.impostos" class="dash-thg dash-thg--com" :colspan="verDetalhe ? 4 : 1">Impostos c/ E-book</th>
                <th class="dash-thg dash-thg--economia dash-th--sortable" :class="{ 'dash-th--sorted': sortKey === 'economia' }" rowspan="2" title="Saving em impostos" @click="sortBy('economia')">
                  <div class="thg-inner thg-inner--center">Saving <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'economia' }">{{ sortIcon('economia') }}</v-icon></div>
                </th>
              </tr>
              <tr class="dash-thead-s">
                <template v-if="grupos.ebooks">
                  <th class="dash-th dash-th--ebook-start dash-th--sortable text-center" :class="{ 'dash-th--sorted': sortKey === 'qtd_ebooks' }" @click="sortBy('qtd_ebooks')">Qtd <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'qtd_ebooks' }">{{ sortIcon('qtd_ebooks') }}</v-icon></th>
                  <th class="dash-th dash-th--ebook dash-th--sortable text-right" :class="{ 'dash-th--sorted': sortKey === 'vlr_ebooks' }" @click="sortBy('vlr_ebooks')">Valor <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'vlr_ebooks' }">{{ sortIcon('vlr_ebooks') }}</v-icon></th>
                  <th class="dash-th dash-th--ebook dash-th--sortable text-right" :class="{ 'dash-th--sorted': sortKey === 'custo_ebooks' }" @click="sortBy('custo_ebooks')">Custo <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'custo_ebooks' }">{{ sortIcon('custo_ebooks') }}</v-icon></th>
                </template>
                <template v-if="grupos.resultado">
                  <th class="dash-th dash-th--resultado-start dash-th--sortable text-right" :class="{ 'dash-th--sorted': sortKey === 'lucro_sem_ebook' }" @click="sortBy('lucro_sem_ebook')">Lucro s/ <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'lucro_sem_ebook' }">{{ sortIcon('lucro_sem_ebook') }}</v-icon></th>
                  <th class="dash-th dash-th--resultado dash-th--sortable text-right" :class="{ 'dash-th--sorted': sortKey === 'lucro_com_ebook' }" @click="sortBy('lucro_com_ebook')">Lucro c/ <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'lucro_com_ebook' }">{{ sortIcon('lucro_com_ebook') }}</v-icon></th>
                  <th class="dash-th dash-th--resultado dash-th--sortable text-right" :class="{ 'dash-th--sorted': sortKey === 'margem_sem_ebook' }" @click="sortBy('margem_sem_ebook')">Margem s/ <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'margem_sem_ebook' }">{{ sortIcon('margem_sem_ebook') }}</v-icon></th>
                  <th class="dash-th dash-th--resultado dash-th--sortable text-right" :class="{ 'dash-th--sorted': sortKey === 'margem_com_ebook' }" @click="sortBy('margem_com_ebook')">Margem c/ <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'margem_com_ebook' }">{{ sortIcon('margem_com_ebook') }}</v-icon></th>
                </template>
                <template v-if="grupos.impostos">
                  <th v-if="verDetalhe" class="dash-th dash-th--sem-start text-right">ICMS</th>
                  <th v-if="verDetalhe" class="dash-th dash-th--sem text-right">PIS</th>
                  <th v-if="verDetalhe" class="dash-th dash-th--sem text-right">COFINS</th>
                  <th class="dash-th dash-th--sem dash-th--total dash-th--sortable text-right" :class="{ 'dash-th--sem-start': !verDetalhe, 'dash-th--sorted': sortKey === 'totalSem' }" @click="sortBy('totalSem')">Total <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'totalSem' }">{{ sortIcon('totalSem') }}</v-icon></th>
                </template>
                <template v-if="grupos.impostos">
                  <th v-if="verDetalhe" class="dash-th dash-th--com-start text-right">ICMS</th>
                  <th v-if="verDetalhe" class="dash-th dash-th--com text-right">PIS</th>
                  <th v-if="verDetalhe" class="dash-th dash-th--com text-right">COFINS</th>
                  <th class="dash-th dash-th--com dash-th--total dash-th--sortable text-right" :class="{ 'dash-th--com-start': !verDetalhe, 'dash-th--sorted': sortKey === 'totalCom' }" @click="sortBy('totalCom')">Total <v-icon x-small class="sort-icon" :class="{ 'sort-icon--active': sortKey === 'totalCom' }">{{ sortIcon('totalCom') }}</v-icon></th>
                </template>
              </tr>
              </thead>

              <!-- Skeleton desktop -->
              <tbody v-if="carregando">
              <tr v-for="n in 6" :key="`sk-${n}`" :style="{ opacity: 1 - n * 0.13 }">
                <td class="dash-td dash-td--rk text-center"><div class="dsk dsk-circle"></div></td>
                <td class="dash-td dash-td--filial"><div class="dsk dsk-text" style="width:110px; margin-bottom:5px;"></div><div class="dsk dsk-chip" style="width:54px;"></div></td>
                <td class="dash-td text-right"><div class="dsk dsk-text" style="width:90px; margin-left:auto;"></div></td>
                <template v-if="grupos.ebooks">
                  <td class="dash-td text-center"><div class="dsk dsk-chip" style="width:34px; margin:auto;"></div></td>
                  <td class="dash-td text-right"><div class="dsk dsk-text" style="width:78px; margin-left:auto;"></div></td>
                  <td class="dash-td text-right"><div class="dsk dsk-text" style="width:78px; margin-left:auto;"></div></td>
                </template>
                <template v-if="grupos.resultado">
                  <td class="dash-td text-right"><div class="dsk dsk-text" style="width:82px; margin-left:auto;"></div></td>
                  <td class="dash-td text-right"><div class="dsk dsk-text" style="width:82px; margin-left:auto;"></div></td>
                  <td class="dash-td text-right"><div class="dsk dsk-text" style="width:46px; margin-left:auto;"></div></td>
                  <td class="dash-td text-right"><div class="dsk dsk-text" style="width:46px; margin-left:auto;"></div></td>
                </template>
                <template v-if="grupos.impostos">
                  <template v-if="verDetalhe">
                    <td class="dash-td text-right"><div class="dsk dsk-text" style="width:70px; margin-left:auto;"></div></td>
                    <td class="dash-td text-right"><div class="dsk dsk-text" style="width:60px; margin-left:auto;"></div></td>
                    <td class="dash-td text-right"><div class="dsk dsk-text" style="width:66px; margin-left:auto;"></div></td>
                  </template>
                  <td class="dash-td text-right"><div class="dsk dsk-text" style="width:86px; margin-left:auto;"></div></td>
                  <template v-if="verDetalhe">
                    <td class="dash-td text-right"><div class="dsk dsk-text" style="width:70px; margin-left:auto;"></div></td>
                    <td class="dash-td text-right"><div class="dsk dsk-text" style="width:60px; margin-left:auto;"></div></td>
                    <td class="dash-td text-right"><div class="dsk dsk-text" style="width:66px; margin-left:auto;"></div></td>
                  </template>
                  <td class="dash-td text-right"><div class="dsk dsk-text" style="width:86px; margin-left:auto;"></div></td>
                </template>
                <td class="dash-td dash-td--economia"><div class="dsk dsk-chip" style="width:54px; margin-bottom:6px;"></div><div class="dsk dsk-text" style="width:96px; margin-bottom:6px;"></div><div class="dsk dsk-bar"></div></td>
              </tr>
              </tbody>

              <tbody v-else>
              <tr
                  v-for="(row, i) in dadosOrdenados"
                  :key="row.idempresa || i"
                  class="dash-row"
                  :class="row.tipo === 'real' ? 'dash-row--est' : 'dash-row--proj'"
              >
                <td class="dash-td dash-td--rk text-center">
                  <span class="rank-badge" :class="rankClass(rankingPorEconomia[row.idempresa])">{{ rankingPorEconomia[row.idempresa] }}</span>
                </td>
                <td class="dash-td dash-td--filial">
                  <div class="filial-nome">{{ row.empresa }}</div>
                  <span class="filial-tipo" :class="'filial-tipo--' + row.tipo">
                    <v-icon style="font-size:8px; margin-right:2px; vertical-align:middle;">{{ row.tipo === 'real' ? 'mdi-check-circle-outline' : 'mdi-trending-up' }}</v-icon>
                    {{ row.tipo === 'real' ? 'Real' : 'Projeção' }}
                  </span>
                </td>
                <td class="dash-td dash-td--venda text-right">{{ row.vlr_venda | formataDinheiro }}</td>
                <template v-if="grupos.ebooks">
                  <td class="dash-td dash-td--ebook dash-td--ebook-start text-center"><span class="qtd-badge">{{ row.qtd_ebooks }}</span></td>
                  <td class="dash-td dash-td--ebook text-right">{{ row.vlr_ebooks | formataDinheiro }}</td>
                  <td class="dash-td dash-td--ebook dash-td--custo text-right">{{ row.custo_ebooks | formataDinheiro }}</td>
                </template>
                <template v-if="grupos.resultado">
                  <td class="dash-td dash-td--resultado dash-td--resultado-start text-right">{{ row.lucro_sem_ebook | formataDinheiro }}</td>
                  <td class="dash-td dash-td--resultado dash-td--resultado-com text-right">{{ row.lucro_com_ebook | formataDinheiro }}</td>
                  <td class="dash-td dash-td--resultado text-right"><span class="margem-val margem-val--sem">{{ row.margem_sem_ebook.toFixed(2) }}%</span></td>
                  <td class="dash-td dash-td--resultado text-right"><span class="margem-val margem-val--com">{{ row.margem_com_ebook.toFixed(2) }}%</span></td>
                </template>
                <template v-if="grupos.impostos">
                  <td v-if="verDetalhe" class="dash-td dash-td--sem dash-td--sem-start text-right">{{ row.icms_sem | formataDinheiro }}</td>
                  <td v-if="verDetalhe" class="dash-td dash-td--sem text-right">{{ row.pis_sem | formataDinheiro }}</td>
                  <td v-if="verDetalhe" class="dash-td dash-td--sem text-right">{{ row.cofins_sem | formataDinheiro }}</td>
                  <td class="dash-td dash-td--sem dash-td--total text-right" :class="{ 'dash-td--sem-start': !verDetalhe }">{{ totalSem(row) | formataDinheiro }}</td>
                </template>
                <template v-if="grupos.impostos">
                  <td v-if="verDetalhe" class="dash-td dash-td--com dash-td--com-start text-right">{{ row.icms_com | formataDinheiro }}</td>
                  <td v-if="verDetalhe" class="dash-td dash-td--com text-right">{{ row.pis_com | formataDinheiro }}</td>
                  <td v-if="verDetalhe" class="dash-td dash-td--com text-right">{{ row.cofins_com | formataDinheiro }}</td>
                  <td class="dash-td dash-td--com dash-td--total text-right" :class="{ 'dash-td--com-start': !verDetalhe }">{{ totalCom(row) | formataDinheiro }}</td>
                </template>
                <td class="dash-td dash-td--economia" :class="{ 'dash-td--saving-est': row.tipo === 'real' }">
                  <span class="eco-tipo" :class="'eco-tipo--' + row.tipo">
                    <v-icon style="font-size:8px; margin-right:2px; vertical-align:middle;">{{ row.tipo === 'real' ? 'mdi-check-circle-outline' : 'mdi-trending-up' }}</v-icon>
                    {{ row.tipo === 'real' ? 'Real' : 'Projeção' }}
                  </span>
                  <div class="eco-bar-label-row">
                    <span class="eco-pct">↓ {{ reducaoPct(row) }}%</span>
                    <span class="eco-val">{{ economiaImposto(row) | formataDinheiro }}</span>
                  </div>
                  <div class="eco-bar-track"><div class="eco-bar" :style="{ width: economiaPct(row) + '%' }"></div></div>
                </td>
              </tr>
              </tbody>

              <tfoot v-if="carregando || totais">
              <tr v-if="carregando" class="dash-tfoot-row">
                <td class="dash-tfoot-td dash-tfoot-td--rk"></td>
                <td class="dash-tfoot-td"><div class="dsk dsk-text" style="width:36px;"></div></td>
                <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:90px;margin-left:auto;"></div></td>
                <template v-if="grupos.ebooks">
                  <td class="dash-tfoot-td text-center"><div class="dsk dsk-chip" style="width:34px;margin:auto;"></div></td>
                  <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:78px;margin-left:auto;"></div></td>
                  <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:78px;margin-left:auto;"></div></td>
                </template>
                <template v-if="grupos.resultado">
                  <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:82px;margin-left:auto;"></div></td>
                  <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:82px;margin-left:auto;"></div></td>
                  <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:46px;margin-left:auto;"></div></td>
                  <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:46px;margin-left:auto;"></div></td>
                </template>
                <template v-if="grupos.impostos">
                  <template v-if="verDetalhe">
                    <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:70px;margin-left:auto;"></div></td>
                    <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:60px;margin-left:auto;"></div></td>
                    <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:66px;margin-left:auto;"></div></td>
                  </template>
                  <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:86px;margin-left:auto;"></div></td>
                  <template v-if="verDetalhe">
                    <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:70px;margin-left:auto;"></div></td>
                    <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:60px;margin-left:auto;"></div></td>
                    <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:66px;margin-left:auto;"></div></td>
                  </template>
                  <td class="dash-tfoot-td text-right"><div class="dsk dsk-text" style="width:86px;margin-left:auto;"></div></td>
                </template>
                <td class="dash-tfoot-td dash-tfoot-td--economia text-right"><div class="dsk dsk-text" style="width:96px;margin-left:auto;"></div></td>
              </tr>
              <tr v-else class="dash-tfoot-row">
                <td class="dash-tfoot-td dash-tfoot-td--rk"></td>
                <td class="dash-tfoot-td dash-tfoot-td--label">Total</td>
                <td class="dash-tfoot-td text-right">{{ totais.vlr_venda | formataDinheiro }}</td>
                <template v-if="grupos.ebooks">
                  <td class="dash-tfoot-td text-center">{{ totais.qtd_ebooks }}</td>
                  <td class="dash-tfoot-td text-right">{{ totais.vlr_ebooks | formataDinheiro }}</td>
                  <td class="dash-tfoot-td text-right">{{ totais.custo_ebooks | formataDinheiro }}</td>
                </template>
                <template v-if="grupos.resultado">
                  <td class="dash-tfoot-td text-right">{{ totais.lucro_sem_ebook | formataDinheiro }}</td>
                  <td class="dash-tfoot-td text-right">{{ totais.lucro_com_ebook | formataDinheiro }}</td>
                  <td class="dash-tfoot-td text-right">{{ totais.margem_sem_ebook.toFixed(2) }}%</td>
                  <td class="dash-tfoot-td text-right">{{ totais.margem_com_ebook.toFixed(2) }}%</td>
                </template>
                <template v-if="grupos.impostos">
                  <td v-if="verDetalhe" class="dash-tfoot-td text-right">{{ totais.icms_sem | formataDinheiro }}</td>
                  <td v-if="verDetalhe" class="dash-tfoot-td text-right">{{ totais.pis_sem | formataDinheiro }}</td>
                  <td v-if="verDetalhe" class="dash-tfoot-td text-right">{{ totais.cofins_sem | formataDinheiro }}</td>
                  <td class="dash-tfoot-td text-right">{{ totalSem(totais) | formataDinheiro }}</td>
                  <td v-if="verDetalhe" class="dash-tfoot-td text-right">{{ totais.icms_com | formataDinheiro }}</td>
                  <td v-if="verDetalhe" class="dash-tfoot-td text-right">{{ totais.pis_com | formataDinheiro }}</td>
                  <td v-if="verDetalhe" class="dash-tfoot-td text-right">{{ totais.cofins_com | formataDinheiro }}</td>
                  <td class="dash-tfoot-td text-right">{{ totalCom(totais) | formataDinheiro }}</td>
                </template>
                <td class="dash-tfoot-td dash-tfoot-td--economia text-right">{{ economiaImposto(totais) | formataDinheiro }}</td>
              </tr>
              </tfoot>
            </table>
          </div>

          <!-- ── Skeleton mobile ─────────────────────────────────────────── -->
          <div v-if="carregando" class="d-block d-md-none dash-mobile-list">
            <div v-for="n in 5" :key="`msk-${n}`" class="dmc dmc-sk" :style="{ opacity: 1 - n * 0.16 }">
              <div class="dmc-header">
                <div class="dsk dsk-circle"></div>
                <div class="dmc-filial">
                  <div class="dsk dsk-text" style="width:130px; margin-bottom:6px;"></div>
                  <div class="dsk dsk-chip" style="width:56px;"></div>
                </div>
                <div style="text-align:right;">
                  <div class="dsk dsk-text" style="width:88px; margin-bottom:5px; margin-left:auto;"></div>
                  <div class="dsk dsk-text" style="width:52px; margin-left:auto;"></div>
                </div>
              </div>
              <div class="dmc-bar-wrap">
                <div class="dsk dsk-bar"></div>
              </div>
              <div class="dmc-body">
                <div v-for="f in 4" :key="f" class="dmc-field">
                  <div class="dsk dsk-text" style="width:60px; margin-bottom:4px;"></div>
                  <div class="dsk dsk-text" style="width:80px;"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Visão: Cards mobile ──────────────────────────────────────── -->
          <div v-if="dados.length" class="d-block d-md-none dash-mobile-list">

            <div
                v-for="(row, i) in dadosOrdenados"
                :key="row.idempresa || i"
                class="dmc"
                :class="row.tipo === 'real' ? 'dmc--est' : 'dmc--proj'"
            >
              <!-- Cabeçalho: rank + filial + saving -->
              <div class="dmc-header">
                <span class="rank-badge" :class="rankClass(rankingPorEconomia[row.idempresa])">{{ rankingPorEconomia[row.idempresa] }}</span>
                <div class="dmc-filial">
                  <span class="dmc-filial-nome">{{ row.empresa }}</span>
                  <span class="filial-tipo" :class="'filial-tipo--' + row.tipo">
                    <v-icon style="font-size:8px; margin-right:2px; vertical-align:middle;">{{ row.tipo === 'real' ? 'mdi-check-circle-outline' : 'mdi-trending-up' }}</v-icon>
                    {{ row.tipo === 'real' ? 'Real' : 'Projeção' }}
                  </span>
                </div>
                <div class="dmc-saving">
                  <div class="dmc-saving-val" :class="row.tipo === 'real' ? 'dmc-saving-val--est' : ''">{{ economiaImposto(row) | formataDinheiro }}</div>
                  <div class="dmc-saving-pct" :class="row.tipo === 'real' ? 'dmc-saving-pct--est' : ''">↓ {{ reducaoPct(row) }}%</div>
                </div>
              </div>

              <!-- Barra de saving -->
              <div class="dmc-bar-wrap">
                <div class="eco-bar-track dmc-bar-track"><div class="eco-bar" :class="row.tipo === 'real' ? 'eco-bar--est' : ''" :style="{ width: economiaPct(row) + '%' }"></div></div>
              </div>

              <!-- Corpo: métricas em grid 2 colunas -->
              <div class="dmc-body">
                <div class="dmc-field">
                  <span class="dmc-label">Venda Total</span>
                  <span class="dmc-val">{{ row.vlr_venda | formataDinheiro }}</span>
                </div>

                <template v-if="grupos.ebooks">
                  <div class="dmc-field">
                    <span class="dmc-label">Qtd E-books</span>
                    <span class="dmc-val dmc-val--blue">{{ row.qtd_ebooks }}</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Valor E-books</span>
                    <span class="dmc-val dmc-val--blue">{{ row.vlr_ebooks | formataDinheiro }}</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Custo E-books</span>
                    <span class="dmc-val dmc-val--red">{{ row.custo_ebooks | formataDinheiro }}</span>
                  </div>
                </template>

                <template v-if="grupos.impostos">
                  <div class="dmc-field">
                    <span class="dmc-label">Imposto s/ E-book</span>
                    <span class="dmc-val dmc-val--red">{{ totalSem(row) | formataDinheiro }}</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Imposto c/ E-book</span>
                    <span class="dmc-val dmc-val--green">{{ totalCom(row) | formataDinheiro }}</span>
                  </div>
                </template>

                <template v-if="grupos.resultado">
                  <div class="dmc-field">
                    <span class="dmc-label">Lucro s/ E-book</span>
                    <span class="dmc-val dmc-val--muted">{{ row.lucro_sem_ebook | formataDinheiro }}</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Lucro c/ E-book</span>
                    <span class="dmc-val dmc-val--purple">{{ row.lucro_com_ebook | formataDinheiro }}</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Margem s/</span>
                    <span class="dmc-val dmc-val--muted">{{ row.margem_sem_ebook.toFixed(2) }}%</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Margem c/</span>
                    <span class="dmc-val dmc-val--purple">{{ row.margem_com_ebook.toFixed(2) }}%</span>
                  </div>
                </template>
              </div>
            </div>

            <!-- Consolidado -->
            <div v-if="totais" class="dmc dmc--total">
              <div class="dmc-header dmc-header--total">
                <div class="dmc-filial">
                  <span class="dmc-filial-nome dmc-filial-nome--total">Total</span>
                </div>
                <div class="dmc-saving">
                  <div class="dmc-saving-val dmc-saving-val--total">{{ economiaImposto(totais) | formataDinheiro }}</div>
                  <div class="dmc-saving-pct dmc-saving-pct--total">↓ {{ reducaoMedia }}%</div>
                </div>
              </div>
              <div class="dmc-body">
                <div class="dmc-field">
                  <span class="dmc-label">Venda Total</span>
                  <span class="dmc-val dmc-val--total">{{ totais.vlr_venda | formataDinheiro }}</span>
                </div>
                <template v-if="grupos.ebooks">
                  <div class="dmc-field">
                    <span class="dmc-label">Qtd E-books</span>
                    <span class="dmc-val dmc-val--total">{{ totais.qtd_ebooks }}</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Valor E-books</span>
                    <span class="dmc-val dmc-val--total">{{ totais.vlr_ebooks | formataDinheiro }}</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Custo E-books</span>
                    <span class="dmc-val dmc-val--total">{{ totais.custo_ebooks | formataDinheiro }}</span>
                  </div>
                </template>
                <template v-if="grupos.impostos">
                  <div class="dmc-field">
                    <span class="dmc-label">Imposto s/ E-book</span>
                    <span class="dmc-val dmc-val--total">{{ totalSem(totais) | formataDinheiro }}</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Imposto c/ E-book</span>
                    <span class="dmc-val dmc-val--total">{{ totalCom(totais) | formataDinheiro }}</span>
                  </div>
                </template>
                <template v-if="grupos.resultado">
                  <div class="dmc-field">
                    <span class="dmc-label">Lucro s/ E-book</span>
                    <span class="dmc-val dmc-val--total">{{ totais.lucro_sem_ebook | formataDinheiro }}</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Lucro c/ E-book</span>
                    <span class="dmc-val dmc-val--total">{{ totais.lucro_com_ebook | formataDinheiro }}</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Margem s/</span>
                    <span class="dmc-val dmc-val--total">{{ totais.margem_sem_ebook.toFixed(2) }}%</span>
                  </div>
                  <div class="dmc-field">
                    <span class="dmc-label">Margem c/</span>
                    <span class="dmc-val dmc-val--total">{{ totais.margem_com_ebook.toFixed(2) }}%</span>
                  </div>
                </template>
              </div>
            </div>

          </div>

          <!-- Erro -->
          <div v-if="erro && !carregando" class="dash-empty">
            <v-icon size="44" color="red lighten-3" class="mb-3">mdi-alert-circle-outline</v-icon>
            <span class="grey--text text--lighten-1 body-2">{{ erro }}</span>
          </div>

          <!-- Vazio -->
          <div v-else-if="!dados.length && !carregando" class="dash-empty">
            <v-icon size="44" color="grey lighten-2" class="mb-3">mdi-chart-areaspline</v-icon>
            <span class="grey--text text--lighten-1 body-2">
              {{ jaConsultou ? 'Nenhum dado encontrado para o período.' : 'Selecione um período e clique em Buscar.' }}
            </span>
          </div>

        </v-card>


      </div>
    </v-col>

  </v-row>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import SeletorPeriodo from '@/Components/UI/Inputs/SeletorPeriodo.vue'


export default {
  name: 'WolihubDashboard',

  components: { SeletorPeriodo },

  computed: {
    ...mapState(['backendUrl']),

    totais() {
      return this.totalRow
    },

    dadosOrdenados() {
      const base = this.filtroFilial
          ? this.dados.filter(r => r.idempresa === this.filtroFilial.idempresa)
          : this.dados
      if (!this.sortKey) return base
      const dir = this.sortDir === 'asc' ? 1 : -1
      const key = this.sortKey
      return [...base].sort((a, b) => {
        const av = this.sortValue(a, key)
        const bv = this.sortValue(b, key)
        if (typeof av === 'string') return av.localeCompare(bv, 'pt-BR') * dir
        return (av - bv) * dir
      })
    },

    maxEconomia() {
      if (!this.dados.length) return 1
      return Math.max(...this.dados.map(r => this.economiaImposto(r))) || 1
    },

    rankingPorEconomia() {
      const pct = r => r.vlr_imposto_sem ? this.economiaImposto(r) / r.vlr_imposto_sem : 0
      const sorted = [...this.dados].sort((a, b) => pct(b) - pct(a))
      const map = {}
      sorted.forEach((row, i) => { map[row.idempresa] = i + 1 })
      return map
    },

    maxImpostoSem() {
      if (!this.dados.length) return 1
      return Math.max(...this.dados.map(r => r.vlr_imposto_sem)) || 1
    },

    colunasOcultas() {
      return [this.grupos.ebooks, this.grupos.resultado, this.grupos.impostos].filter(v => !v).length
    },

    reducaoMedia() {
      if (!this.totais || !this.totais.vlr_imposto_sem) return '0.0'
      return (this.economiaImposto(this.totais) / this.totais.vlr_imposto_sem * 100).toFixed(1)
    },

    contagemFiliais() {
      const real = this.dados.filter(r => r.tipo === 'real').length
      const proj = this.dados.length - real
      return { real, proj }
    },

    eficienciaHeight() {
      return Math.max(200, this.dados.length * 30 + 60)
    },

    top5Height() {
      return Math.max(140, Math.min(this.dados.length, 5) * 36 + 30)
    },

    bottom5Height() {
      return Math.max(140, Math.min(this.dados.length, 5) * 36 + 30)
    },

    chartEficienciaSorted() {
      return [...this.dados].sort((a, b) => {
        const pA = a.vlr_saving / (a.vlr_imposto_sem || 1)
        const pB = b.vlr_saving / (b.vlr_imposto_sem || 1)
        return pB - pA
      })
    },

    chartEficienciaSeries() {
      const sorted = this.chartEficienciaSorted
      return [
        { name: 'Economizado', data: sorted.map(r => parseFloat((r.vlr_saving / (r.vlr_imposto_sem || 1) * 100).toFixed(1))) },
        { name: 'Imposto Pago', data: sorted.map(r => parseFloat((r.vlr_imposto_com / (r.vlr_imposto_sem || 1) * 100).toFixed(1))) },
      ]
    },

    chartEficienciaOptions() {
      const vm = this
      const sorted = this.chartEficienciaSorted
      const fmt = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)
      return {
        chart: {
          type: 'bar', stacked: true, stackType: '100%',
          toolbar: { show: false }, fontFamily: 'inherit',
          events: {
            dataPointSelection(e, ctx, { dataPointIndex }) {
              const filial = sorted[dataPointIndex]
              vm.filtroFilial = vm.filtroFilial?.idempresa === filial.idempresa ? null : filial
            },
          },
        },
        plotOptions: { bar: { horizontal: true, borderRadius: 0, barHeight: '65%' } },
        colors: ['#43a047', '#ef5350'],
        xaxis: {
          categories: sorted.map(r => r.empresa),
          labels: { formatter: v => v + '%', style: { fontSize: '10px' } },
        },
        yaxis: { labels: { style: { fontSize: '11px' } } },
        tooltip: {
          custom({ dataPointIndex }) {
            const r = sorted[dataPointIndex]
            const pct = (r.vlr_saving / (r.vlr_imposto_sem || 1) * 100).toFixed(1)
            return `<div style="padding:8px 10px;font-size:11px;font-family:inherit;line-height:1.8">
              <div style="font-weight:700;color:#023449;margin-bottom:4px">${r.empresa}</div>
              <div><span style="color:#43a047">●</span> Economizado: <strong>${fmt(r.vlr_saving)}</strong> (${pct}%)</div>
              <div><span style="color:#ef5350">●</span> Imposto Pago: <strong>${fmt(r.vlr_imposto_com)}</strong></div>
            </div>`
          },
        },
        legend: { position: 'top', fontSize: '10px', markers: { radius: 3 } },
        dataLabels: {
          enabled: true,
          formatter(val, opts) {
            if (val < 8) return ''
            const r = sorted[opts.dataPointIndex]
            if (opts.seriesIndex === 0) {
              const k = (r.vlr_saving / 1000).toFixed(0)
              return `${val.toFixed(0)}%  ·  R$ ${k}k`
            }
            const k = (r.vlr_imposto_com / 1000).toFixed(0)
            return `${val.toFixed(0)}%  ·  R$ ${k}k`
          },
          style: { fontSize: '10px', fontWeight: '600', colors: ['#fff', '#fff'] },
        },
        grid: { borderColor: '#f1f5f9' },
      }
    },

    chartTop5Series() {
      const sorted = [...this.dados].sort((a, b) => b.vlr_saving - a.vlr_saving).slice(0, 5)
      return [{ name: 'Saving', data: sorted.map(r => r.vlr_saving) }]
    },

    chartTop5Options() {
      const vm = this
      const sorted = [...this.dados].sort((a, b) => b.vlr_saving - a.vlr_saving).slice(0, 5)
      const fmt = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)
      return {
        chart: {
          type: 'bar', toolbar: { show: false }, fontFamily: 'inherit',
          events: {
            dataPointSelection(e, ctx, { dataPointIndex }) {
              const filial = sorted[dataPointIndex]
              vm.filtroFilial = vm.filtroFilial?.idempresa === filial.idempresa ? null : filial
            },
          },
        },
        plotOptions: { bar: { horizontal: true, borderRadius: 3, distributed: true, barHeight: '65%' } },
        colors: ['#1b5e20', '#2e7d32', '#388e3c', '#43a047', '#66bb6a'],
        xaxis: { categories: sorted.map(r => r.empresa), labels: { style: { fontSize: '10px' } } },
        yaxis: { labels: { style: { fontSize: '10px' } } },
        tooltip: { y: { formatter: fmt } },
        legend: { show: false },
        dataLabels: { enabled: false },
        grid: { borderColor: '#f1f5f9', xaxis: { lines: { show: true } } },
      }
    },

    chartBottom5Series() {
      const sorted = [...this.dados].sort((a, b) => a.vlr_saving - b.vlr_saving).slice(0, 5)
      return [{ name: 'Saving', data: sorted.map(r => r.vlr_saving) }]
    },

    chartBottom5Options() {
      const vm = this
      const sorted = [...this.dados].sort((a, b) => a.vlr_saving - b.vlr_saving).slice(0, 5)
      const fmt = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)
      return {
        chart: {
          type: 'bar', toolbar: { show: false }, fontFamily: 'inherit',
          events: {
            dataPointSelection(e, ctx, { dataPointIndex }) {
              const filial = sorted[dataPointIndex]
              vm.filtroFilial = vm.filtroFilial?.idempresa === filial.idempresa ? null : filial
            },
          },
        },
        plotOptions: { bar: { horizontal: true, borderRadius: 3, distributed: true, barHeight: '65%' } },
        colors: ['#b71c1c', '#c62828', '#d32f2f', '#e53935', '#ef5350'],
        xaxis: { categories: sorted.map(r => r.empresa), labels: { style: { fontSize: '10px' } } },
        yaxis: { labels: { style: { fontSize: '10px' } } },
        tooltip: { y: { formatter: fmt } },
        legend: { show: false },
        dataLabels: { enabled: false },
        grid: { borderColor: '#f1f5f9', xaxis: { lines: { show: true } } },
      }
    },

  },

  data() {
    const hoje = new Date()
    const fmt  = d => d.toISOString().slice(0, 10)
    return {
      busca:         { periodo: {
          inicio: fmt(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
          fim:    fmt(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)),
        }},
      carregando:    false,
      jaConsultou:   false,
      dados:         [],
      totalRow:      null,
      erro:          null,
      verDetalhe:    false,
      grupos:        { ebooks: true, impostos: true, resultado: false },
      sortKey:       'rk',
      sortDir:       'desc',
      isFullscreen:  false,
      mostrarFiltro: true,
      filtroFilial:  null,
      alertaFiscal:  true,
    }
  },

  mounted() {
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
  },

  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
  },

  methods: {
    toggleFullscreen() {
      const el = this.$refs.dashCard
      if (!document.fullscreenElement) {
        el.requestFullscreen()
      } else {
        document.exitFullscreen()
      }
    },

    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement
    },

    buscar() {
      if (!this.busca.periodo || !this.busca.periodo.inicio) return
      this.carregando    = true
      this.jaConsultou   = true
      this.erro          = null
      this.mostrarFiltro = false

      axios
          .post(`${this.backendUrl}wolihub/saving/listar`, {
            dtinicio: this.busca.periodo.inicio,
            dtfim:    this.busca.periodo.fim,
          })
          .then(res => {
            const all     = Array.isArray(res.data) ? res.data : []
            this.totalRow = all.find(r => r.idempresa === 0) || null
            this.dados    = all.filter(r => r.idempresa !== 0)
          })
          .catch(e => {
            this.dados    = []
            this.totalRow = null
            this.erro     = this.erroFormatar(e)
          })
          .finally(() => { this.carregando = false })
    },

    totalSem(row) { return row.vlr_imposto_sem || 0 },
    totalCom(row) { return row.vlr_imposto_com || 0 },
    economiaImposto(row) {
      if (row.vlr_saving != null) return row.vlr_saving
      return (row.vlr_imposto_sem || 0) - (row.vlr_imposto_com || 0) - (row.custo_ebooks || 0)
    },
    economiaPct(row) { return (this.economiaImposto(row) / this.maxEconomia * 100).toFixed(1) },

    sortValue(row, key) {
      if (key === 'rk')      return row.vlr_imposto_sem ? this.economiaImposto(row) / row.vlr_imposto_sem : 0
      if (key === 'empresa') return row.empresa || ''
      if (key === 'economia') return this.economiaImposto(row)
      return row[key] || 0
    },

    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'desc'
      }
    },

    sortIcon(key) {
      if (this.sortKey !== key) return 'mdi-unfold-more-horizontal'
      return this.sortDir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'
    },

    impactoPct(row, tipo) {
      const val = tipo === 'sem' ? row.vlr_imposto_sem : row.vlr_imposto_com
      return (val / this.maxImpostoSem * 100).toFixed(1)
    },

    reducaoPct(row) {
      if (!row.vlr_imposto_sem) return '0.0'
      return (this.economiaImposto(row) / row.vlr_imposto_sem * 100).toFixed(1)
    },

    rankClass(rank) {
      if (rank === 1) return 'rank-badge--1'
      if (rank === 2) return 'rank-badge--2'
      if (rank === 3) return 'rank-badge--3'
      return 'rank-badge--n'
    },
  },
}
</script>

<style scoped>
/* ── KPI cards de resumo ──────────────────────────────────────────────────── */
.kpi-card {
  border-radius: 10px;
  border: 0.5px solid #dde4ed;
  border-left: 3px solid #023449;
  background: #fff;
  overflow: hidden;
  height: 100%;
}

.kpi-body { padding: 10px 13px; }

.kpi-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.kpi-ico {
  width: 24px; height: 24px; border-radius: 6px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.kpi-ico--primary { background: rgba(2, 52, 73, 0.07); }

.kpi-head-info { flex: 1; min-width: 0; }
.kpi-title {
  font-size: 9px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8;
}
.kpi-main {
  font-size: 15px; font-weight: 700; line-height: 1.2;
  font-variant-numeric: tabular-nums; color: #023449;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.kpi-sep { height: 0.5px; background: #f1f5f9; margin-bottom: 7px; }

.kpi-row { display: flex; justify-content: space-between; align-items: center; padding: 2px 0; }
.kpi-row-label { font-size: 10px; color: #94a3b8; display: flex; align-items: center; gap: 3px; }
.kpi-row-val { font-size: 11px; font-weight: 600; color: #374151; font-variant-numeric: tabular-nums; }
.kpi-val--primary { color: #023449; }
.kpi-val--green   { color: #2e7d32; }
.kpi-val--red     { color: #c62828; }

/* Dot + barras */
.kpi-dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.kpi-dot--sem { background: #ef9a9a; }
.kpi-dot--com { background: #81c784; }

.kpi-bar-wrap { padding: 2px 0; }
.kpi-bar-track { height: 3px; background: #f1f5f9; border-radius: 2px; overflow: hidden; }
.kpi-bar { height: 100%; border-radius: 2px; transition: width 0.5s ease; }
.kpi-bar--sem   { background: #ef9a9a; }
.kpi-bar--com   { background: #81c784; }
.kpi-bar--ebook { background: #023449; }

/* Participação e-books */
.kpi-share-sub {
  display: flex; justify-content: space-between;
  font-size: 9px; color: #94a3b8; margin-top: 3px;
}
.kpi-share-sub .kpi-val--primary { font-size: 9px; }

/* ── Container principal ──────────────────────────────────────────────────── */
.dash-container {
  display: flex; flex-direction: column; gap: 14px; position: relative;
  background: #eef2f6;
  border-radius: 14px;
}
.dash-content-card { border-radius: 10px !important; overflow: hidden; display: flex; flex-direction: column; }

/* ── Cabeçalho flutuante ──────────────────────────────────────────────────── */
.dash-header {
  background: linear-gradient(135deg, #023449 0%, #035878 100%);
  padding: 12px 16px;
  display: flex; align-items: center; gap: 12px;
  color: white;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(2, 52, 73, 0.10);
}
.dash-header-icon {
  width: 32px; height: 32px; border-radius: 8px;
  background: rgba(255,255,255,0.12);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.dash-header-title { font-size: 13px; font-weight: 700; }
.dash-header-sub   { font-size: 11px; color: rgba(255,255,255,0.6); margin-top: 1px; }



/* ── Botões de ação do cabeçalho (filtro, fullscreen) ─────────────────────── */
.dash-filter-btn {
  border-radius: 8px !important;
  background: rgba(255,255,255,0.06) !important;
  border: 1px solid rgba(255,255,255,0.15) !important;
  color: rgba(255,255,255,0.4) !important;
  transition: background 0.2s, border-color 0.2s, color 0.2s !important;
}
.dash-filter-btn:hover {
  background: rgba(255,255,255,0.14) !important;
  color: rgba(255,255,255,0.75) !important;
}
.dash-filter-btn--active {
  background: rgba(255,255,255,0.18) !important;
  border-color: rgba(255,255,255,0.45) !important;
  color: #fff !important;
}

/* ── Fullscreen ────────────────────────────────────────────────────────────── */
.dash-container--fullscreen:fullscreen,
.dash-container--fullscreen:-webkit-full-screen {
  height: 100%;
  overflow: hidden;
  background: #eef2f6;
  padding: 20px;
  border-radius: 0;
  gap: 14px;
}
.dash-container--fullscreen::backdrop {
  background: #eef2f6;
}
.dash-container--fullscreen:fullscreen .dash-kpi-area,
.dash-container--fullscreen:-webkit-full-screen .dash-kpi-area {
  grid-template-columns: repeat(3, 1fr);
}
.dash-container--fullscreen:fullscreen .dash-content-card,
.dash-container--fullscreen:-webkit-full-screen .dash-content-card {
  flex: 1;
  min-height: 0;
  overflow: hidden !important;
}
.dash-container--fullscreen:fullscreen .dash-table-wrap,
.dash-container--fullscreen:-webkit-full-screen .dash-table-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

/* ── Painel de filtros ────────────────────────────────────────────────────── */
.dash-filter-wrap {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows 0.25s ease, margin-bottom 0.25s ease;
}
.dash-filter-wrap--hidden {
  grid-template-rows: 0fr;
  margin-bottom: -14px;
}
.dash-filter-clip {
  overflow: hidden;
}
.dash-filter {
  background: #fff;
  border: 0.5px solid #dde4ed;
  border-radius: 10px;
  padding: 10px 12px 8px;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.06);
}

/* ── KPI row flutuante ────────────────────────────────────────────────────── */
.dash-kpi-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.dash-kpi-row .kpi-card {
  height: auto;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.07);
}

@media (max-width: 600px) {
  .dash-kpi-row { grid-template-columns: 1fr; }
}

/* ── Toolbar ──────────────────────────────────────────────────────────────── */
.dash-toolbar {
  display: flex; align-items: center; justify-content: flex-end;
  padding: 7px 14px;
  background: #f8fafc;
  border-bottom: 1px solid #e8edf2;
}
.col-menu-wrap { position: relative; display: inline-block; }
.col-menu-btn {
  display: inline-flex; align-items: center;
  padding: 4px 11px; border-radius: 6px;
  font-size: 12px; font-weight: 600; color: #023449;
  background: #fff; border: 1px solid #dde4ed;
  cursor: pointer; font-family: inherit;
  transition: background 0.15s, border-color 0.15s;
  position: relative;
}
.col-menu-btn:hover { background: #f1f5f9; border-color: #c5d0db; }
.col-menu-badge {
  position: absolute; top: -6px; right: -6px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; border-radius: 50%;
  background: #023449; color: #fff;
  font-size: 9px; font-weight: 700;
  pointer-events: none;
}
.col-menu-list { background: #fff; }
.col-menu-item { min-height: 36px !important; cursor: pointer; }
.col-menu-item:hover { background: #f8fafc; }
.col-menu-item--sub { padding-left: 8px !important; }
.col-menu-label { font-size: 12px !important; font-weight: 500; color: #374151; }
.col-menu-label--sub { font-size: 11px !important; color: #64748b; }
.col-menu-sub { font-size: 10px !important; color: #94a3b8; margin-top: 1px; }

/* ── Tabela ───────────────────────────────────────────────────────────────── */
.dash-table-wrap { overflow-x: auto; }
.dash-table { width: 100%; border-collapse: collapse; white-space: nowrap; background: #fff; }

.dash-thead-g { background: #f8fafc; }
.dash-table thead { position: sticky; top: 0; z-index: 5; }
.dash-thead-g th { background: #f8fafc; }
.dash-thg {
  padding: 8px 10px; font-size: 9px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8;
  border-bottom: 1px solid #e8edf2; text-align: center; vertical-align: bottom;
}
.dash-thg-note { font-size: 8px; font-weight: 500; text-transform: lowercase; letter-spacing: 0; color: #b0bec5; margin-top: 2px; }
.dash-thg--filial   { text-align: left; min-width: 155px; vertical-align: middle !important; }
.dash-thg--venda    { text-align: right; min-width: 110px; color: #64748b; border-left: 1px solid #e8edf2; }
.dash-thg--ebook    { color: #1565c0; border-left: 2px solid #90caf9; }
.dash-thg--sem      { color: #c62828; border-left: 2px solid #ef9a9a; }
.dash-thg--com      { color: #2e7d32; border-left: 2px solid #81c784; }
.dash-thg--resultado { color: #4527a0; border-left: 2px solid #b39ddb; }
.dash-thg--economia { text-align: right; min-width: 120px; color: #bf360c; border-left: 2px solid #ffa726; background: #fff8f0; }
.dash-thg--rk { width: 48px; min-width: 48px; text-align: center; vertical-align: middle !important; }

.thg-inner { display: inline-flex; align-items: center; }
.thg-inner--center { justify-content: center; }

.dash-thead-s { background: #f1f5f9; }
.dash-thead-s th { background: #f1f5f9; vertical-align: top; }
.dash-th {
  padding: 5px 10px; font-size: 9px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.4px; color: #b0bec5;
  border-bottom: 2px solid #e2e8f0; border-left: 1px solid #edf2f7; white-space: nowrap;
}
.dash-th--ebook { color: #5c8fd6; }
.dash-th--sem   { color: #e57373; }
.dash-th--com   { color: #66bb6a; }
.dash-th--total { font-weight: 700; }
.dash-th--resultado { color: #9575cd; border-left: 1px solid #ede7f6; }
.dash-th--ebook-start     { border-left: 2px solid #90caf9; color: #5c8fd6; }
.dash-th--sem-start       { border-left: 2px solid #ffcdd2; color: #e57373; }
.dash-th--com-start       { border-left: 2px solid #c8e6c9; color: #66bb6a; }
.dash-th--resultado-start { border-left: 2px solid #b39ddb; color: #9575cd; }

.dash-th--sortable { cursor: pointer; user-select: none; }
.dash-th--sortable:hover { background: #e8edf4; }
.dash-th--sorted { color: #374151 !important; background: #eef2f7; }
.dash-thg.dash-th--sortable { cursor: pointer; user-select: none; }
.dash-thg.dash-th--sortable:hover { filter: brightness(0.97); }
.dash-thg.dash-th--sorted { color: #334155 !important; }
.sort-icon { opacity: 0.3; margin-left: 2px; vertical-align: middle !important; }
.sort-icon--active { opacity: 1; color: #1976d2 !important; }

.dash-row { transition: background 0.1s ease; }
.dash-row:nth-child(even) { background: #fafbfc; }
.dash-row:hover { background: #f0f7ff !important; }
.dash-row--est .dash-td--rk { box-shadow: inset 3px 0 0 #66bb6a; }
.dash-row--proj .dash-td--rk { box-shadow: inset 3px 0 0 #ffb74d; }

.dash-td {
  padding: 9px 10px; font-size: 11.5px; color: #374151;
  border-bottom: 1px solid #f1f5f9; vertical-align: middle; font-variant-numeric: tabular-nums;
}
.dash-td--rk     { width: 48px; min-width: 48px; padding: 9px 4px !important; }
.dash-td--filial { min-width: 155px; }
.dash-td--venda  { font-weight: 600; color: #0f172a; border-left: 1px solid #f1f5f9; }
.dash-td--ebook  { color: #1976d2; border-left: 1px solid #e8f4fd; }
.dash-td--custo  { color: #c62828; }
.dash-td--total  { font-weight: 700; }
.dash-td--sem    { color: #c62828; }
.dash-td--com    { color: #2e7d32; }
.dash-td--ebook-start     { border-left: 2px solid #bbdefb; }
.dash-td--sem-start       { border-left: 2px solid #ffcdd2 !important; }
.dash-td--com-start       { border-left: 2px solid #c8e6c9 !important; }
.dash-td--resultado-start { border-left: 2px solid #d1c4e9 !important; }
.dash-td--resultado       { border-left: 1px solid #f3e5f5; }
.dash-td--resultado-com   { font-weight: 700; color: #4527a0; }

.dash-td--economia { background: #fff8f0; border-left: 2px solid #ffa726; min-width: 130px; }
.dash-td--saving-est { background: #f1f8f2; border-left-color: #66bb6a; }

.eco-tipo {
  display: inline-flex; align-items: center; font-size: 8px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.4px;
  padding: 1px 5px; border-radius: 4px; margin-bottom: 4px; white-space: nowrap;
}
.eco-tipo--real { color: #2e7d32; border: 1px solid #a5d6a7; }
.eco-tipo--projecao { color: #bf360c; border: 1px solid #ffcc80; }
.eco-val { font-weight: 700; color: #e65100; font-variant-numeric: tabular-nums; font-size: 12px; }
.dash-td--saving-est .eco-val { color: #1b5e20; }
.eco-bar-track { height: 4px; background: #ffe0b2; border-radius: 2px; margin-top: 5px; }
.dash-td--saving-est .eco-bar-track { background: #c8e6c9; }
.eco-bar { height: 100%; background: #ffa726; border-radius: 2px; min-width: 2px; transition: width 0.4s ease; }
.dash-td--saving-est .eco-bar { background: #66bb6a; }

.filial-nome { font-weight: 600; font-size: 12px; color: #0f172a; }
.filial-tipo {
  display: inline-flex; align-items: center; font-size: 8.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.4px; padding: 1px 6px; border-radius: 10px;
  margin-top: 3px; white-space: nowrap;
}
.filial-tipo--real { color: #2e7d32; background: #e8f5e9; }
.filial-tipo--projecao { color: #bf360c; background: #fff3e0; }

.rank-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%;
  font-weight: 700; font-size: 11px; flex-shrink: 0; font-variant-numeric: tabular-nums;
}
.rank-badge--1 { background: #fef3c7; color: #92400e; border: 1.5px solid #fbbf24; }
.rank-badge--2 { background: #f1f5f9; color: #334155; border: 1.5px solid #94a3b8; }
.rank-badge--3 { background: #fdf2e0; color: #78350f; border: 1.5px solid #d97706; }
.rank-badge--n { background: #f8fafc; color: #94a3b8; border: 1.5px solid #e2e8f0; }

.qtd-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 28px; height: 20px; padding: 0 5px; border-radius: 4px;
  font-weight: 700; font-size: 11px; color: #1565c0; background: #e3f2fd; border: 1px solid #bbdefb;
  font-variant-numeric: tabular-nums;
}

.margem-val { font-variant-numeric: tabular-nums; }
.margem-val--sem { color: #90a4ae; }
.margem-val--com { font-weight: 700; color: #1565c0; }

.dash-tfoot-row { background: #1a3a4a; }
.dash-tfoot-td {
  padding: 9px 10px; font-size: 11.5px; color: rgba(255,255,255,0.75);
  border-top: 2px solid rgba(0,0,0,0.2); font-variant-numeric: tabular-nums; vertical-align: middle;
}
.dash-tfoot-td--rk    { width: 48px; }
.dash-tfoot-td--label { font-weight: 700; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: rgba(255,255,255,0.45); }
.dash-tfoot-td--economia { background: rgba(255,167,38,0.12); color: #ffb74d; font-weight: 700; }

/* ── Chip de filtro ativo ─────────────────────────────────────────────────── */
.dash-filter-chip { font-weight: 600 !important; }
.fade-chip-enter-active, .fade-chip-leave-active { transition: opacity 0.2s, transform 0.2s; }
.fade-chip-enter, .fade-chip-leave-to { opacity: 0; transform: scale(0.85); }

/* ── Divisor com acento lateral ───────────────────────────────────────────── */
.dash-section-divider {
  display: flex; align-items: center;
}
.dash-section-divider-line { display: none; }
.dash-section-divider-label {
  display: inline-flex; align-items: center;
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.6px;
  color: #64748b; white-space: nowrap;
  padding-left: 8px;
  border-left: 2.5px solid #023449;
  line-height: 1.2;
}
.dash-section-divider-hint {
  font-weight: 400; text-transform: none; letter-spacing: 0;
  font-style: italic; color: #b0bec5; margin-left: 5px; font-size: 9px;
}

/* ── Gráficos flutuantes ──────────────────────────────────────────────────── */
.dash-analysis-grid {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 12px;
}
@media (max-width: 960px) {
  .dash-analysis-grid { grid-template-columns: 1fr; }
}

.dash-float-chart {
  background: #fff;
  border: 0.5px solid #dde4ed;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.07);
  padding: 12px 12px 6px;
  overflow: hidden;
}
.dash-float-chart-title {
  font-size: 10px; font-weight: 700;
  color: #64748b; margin-bottom: 6px;
  display: flex; align-items: center;
}
.dash-float-chart-title--green { color: #2e7d32; }
.dash-float-chart-title--red   { color: #c62828; }

.analysis-rankings {
  display: flex; flex-direction: column; gap: 10px;
}


/* ── Estado vazio ─────────────────────────────────────────────────────────── */
.dash-empty {
  display: flex; flex-direction: column; align-items: center;
  padding: 48px 16px;
}

/* ── Banner dados mockados ────────────────────────────────────────────────── */
.dash-mock-banner {
  display: flex; align-items: center;
  padding: 6px 16px;
  background: #f57c00;
  font-size: 11px; font-weight: 600; color: #fff;
}

/* ── Alerta informativo ───────────────────────────────────────────────────── */
@keyframes alert-icon-blink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.2; transform: scale(0.85); }
}

.dash-alert-wrap {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: linear-gradient(135deg, #e8f4fd 0%, #dbeafe 100%);
  border: 1.5px solid #90caf9;
  border-left: 5px solid #0477a1;
  border-radius: 10px;
  padding: 16px 16px 16px 18px;
  color: #023449;
  line-height: 1.6;
  box-shadow: 0 2px 8px rgba(4, 119, 161, 0.12);
}
.dash-alert-ico-wrap {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  background: rgba(4, 119, 161, 0.10);
  border: 1.5px solid rgba(4, 119, 161, 0.25);
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: alert-icon-blink 1.8s ease-in-out infinite;
}
.dash-alert-txt { flex: 1; }
.dash-alert-title {
  font-size: 12.5px;
  font-weight: 800;
  color: #023449;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.dash-alert-body {
  font-size: 12px;
  color: #1e4f6b;
  line-height: 1.65;
}
.dash-alert-body strong { font-weight: 700; color: #023449; }
.dash-alert-close {
  flex-shrink: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 3px 4px;
  border-radius: 5px;
  opacity: 0.45;
  transition: opacity 0.15s, background 0.15s;
  font-family: inherit;
  line-height: 1;
  margin-top: 2px;
}
.dash-alert-close:hover { opacity: 1; background: rgba(4, 119, 161, 0.12); }

.dash-alert-enter-active, .dash-alert-leave-active {
  transition: opacity 0.2s ease, max-height 0.28s ease, padding 0.28s ease;
  max-height: 160px;
  overflow: hidden;
}
.dash-alert-enter, .dash-alert-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/* ══════════════════════════════════════════════════════════════════════════
   HERO SAVING FISCAL
══════════════════════════════════════════════════════════════════════════ */
.dash-kpi-area {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  align-items: start;
}

@media (max-width: 600px) {
  .dash-kpi-area { grid-template-columns: 1fr; }
}

.kpi-hero {
  background: linear-gradient(135deg, #023449 0%, #035878 100%);
  border-radius: 12px;
  min-width: 240px;
  box-shadow: 0 2px 8px rgba(2, 52, 73, 0.18);
  overflow: hidden;
  position: relative;
}

.kpi-hero::after {
  content: '';
  position: absolute;
  right: -24px;
  bottom: -32px;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  pointer-events: none;
}

.kpi-hero-body { padding: 16px 18px; position: relative; z-index: 1; }

.kpi-hero-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.kpi-ico--hero {
  width: 34px;
  height: 34px;
  background: rgba(255, 255, 255, 0.13);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.kpi-hero-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(255, 255, 255, 0.55);
}

.kpi-hero-sub {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 1px;
  line-height: 1.3;
}

.kpi-hero-val {
  font-size: 29px;
  font-weight: 800;
  color: #ffffff;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  margin-bottom: 0;
}

.kpi-hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(102, 187, 106, 0.18);
  border: 1px solid rgba(102, 187, 106, 0.3);
  color: #81c784;
  border-radius: 7px;
  padding: 4px 9px;
  font-size: 11px;
  font-weight: 700;
}

/* ── Grid de KPIs de suporte ──────────────────────────────────────────────── */
.kpi-support-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

@media (max-width: 600px) {
  .kpi-support-grid { grid-template-columns: 1fr; }
}

/* ── Chips de grupo na toolbar ────────────────────────────────────────────── */
.grupo-chips {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.grupo-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid #dde4ed;
  background: #f1f5f9;
  color: #94a3b8;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  user-select: none;
}

.grupo-chip--on {
  background: #e8f4fd;
  border-color: #90caf9;
  color: #1565c0;
}

.grupo-chip--sub { font-size: 10px; padding: 4px 8px; }

.grupo-chip--sub.grupo-chip--on {
  background: #e8f5e9;
  border-color: #a5d6a7;
  color: #2e7d32;
}

.grupo-chip:hover:not(.grupo-chip--on) {
  background: #e8edf4;
  color: #64748b;
  border-color: #c5d0db;
}

.grupo-sep {
  color: #dde4ed;
  font-size: 14px;
  padding: 0 2px;
  user-select: none;
  line-height: 1;
}

/* ── Percentual de redução na célula de saving ────────────────────────────── */
.eco-bar-label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
  margin-top: 5px;
  margin-bottom: 4px;
}

.eco-pct {
  font-size: 12px;
  font-weight: 800;
  color: #e65100;
  letter-spacing: 0.2px;
}

.dash-td--saving-est .eco-pct { color: #2e7d32; }

/* ── Tags real / real no card de Saving ──────────────────────────────── */
.kpi-filiais-detail { display: flex; gap: 5px; margin-top: 6px; flex-wrap: wrap; }

.kpi-filial-tag {
  font-size: 9px; font-weight: 700; padding: 2px 7px;
  border-radius: 4px; text-transform: uppercase; letter-spacing: 0.3px;
}
.kpi-filial-tag--real { background: #e8f5e9; color: #2e7d32; }
.kpi-filial-tag--proj { background: #fff3e0; color: #bf360c; }

/* ── Hero stats row ──────────────────────────────────────────────────────── */
.kpi-hero-sep {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 12px 0 10px;
}

.kpi-hero-stats {
  display: flex;
  align-items: center;
}

.kpi-hero-stat { flex: 1; text-align: center; }

.kpi-hero-stat-sep {
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.12);
  flex-shrink: 0;
}

.kpi-hero-stat-val {
  font-size: 15px;
  font-weight: 800;
  color: #fff;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

.kpi-hero-stat-label {
  font-size: 8.5px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 3px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* ── Card variantes por tipo ─────────────────────────────────────────────── */
.kpi-card { transition: box-shadow 0.18s ease; }
.kpi-card:hover { box-shadow: 0 2px 8px rgba(2, 52, 73, 0.1); }

.kpi-card--fat { border-left-color: #1565c0; }
.kpi-card--imp { border-left-color: #c62828; }

.kpi-ico--fat { background: rgba(21, 101, 192, 0.07); }
.kpi-ico--imp { background: rgba(198, 40, 40, 0.07); }

/* ── Badge de redução no card de impostos ────────────────────────────────── */
.kpi-reduc-badge {
  display: inline-flex;
  align-items: center;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  color: #2e7d32;
  border-radius: 5px;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  margin-left: auto;
}

/* ── Cards mobile ─────────────────────────────────────────────────────────── */
.dash-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.dmc {
  border-bottom: 1px solid #edf2f6;
  background: #fff;
}
.dmc:last-child { border-bottom: none; }
.dmc--est  { border-left: 3px solid #66bb6a; }
.dmc--proj { border-left: 3px solid #ffb74d; }
.dmc--total { background: #1a3a4a; border-left: none; }

.dmc-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px 6px;
}
.dmc-header--total { padding: 12px 14px; }

.dmc-filial { flex: 1; min-width: 0; }
.dmc-filial-nome {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dmc-filial-nome--total {
  color: rgba(255,255,255,0.9);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dmc-saving { text-align: right; flex-shrink: 0; }
.dmc-saving-val {
  font-size: 14px;
  font-weight: 800;
  color: #e65100;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}
.dmc-saving-val--est   { color: #2e7d32; }
.dmc-saving-val--total { color: #ffb74d; font-size: 15px; }

.dmc-saving-pct {
  font-size: 11px;
  font-weight: 700;
  color: #e65100;
  margin-top: 1px;
}
.dmc-saving-pct--est   { color: #2e7d32; }
.dmc-saving-pct--total { color: rgba(255,183,77,0.8); }

.dmc-bar-wrap { padding: 0 14px 8px; }
.dmc-bar-track { height: 5px !important; }
.eco-bar--est { background: #66bb6a !important; }

.dmc-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 0 14px 10px;
}

.dmc-field {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 5px 6px 5px 0;
  border-bottom: 1px solid #f1f5f9;
}
.dmc-field:nth-last-child(-n+2) { border-bottom: none; }
.dmc--total .dmc-field { border-bottom-color: rgba(255,255,255,0.07); }

.dmc-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #94a3b8;
}
.dmc--total .dmc-label { color: rgba(255,255,255,0.35); }

.dmc-val {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  font-variant-numeric: tabular-nums;
}
.dmc-val--blue   { color: #1565c0; }
.dmc-val--red    { color: #c62828; }
.dmc-val--green  { color: #2e7d32; }
.dmc-val--purple { color: #4527a0; font-weight: 700; }
.dmc-val--muted  { color: #90a4ae; }
.dmc-val--total  { color: rgba(255,255,255,0.85); }

.dmc-sk { pointer-events: none; }

/* ── Shimmer ──────────────────────────────────────────────────────────────── */
@keyframes dsk-shimmer {
  0%   { background-position: -500px 0; }
  100% { background-position:  500px 0; }
}
.dsk {
  display: block;
  border-radius: 4px;
  background: linear-gradient(90deg, #dde6ee 25%, #edf3f8 50%, #dde6ee 75%);
  background-size: 500px 100%;
  animation: dsk-shimmer 1.4s infinite linear;
}
.dsk-text   { height: 12px; }
.dsk-chip   { height: 18px; border-radius: 9px; }
.dsk-circle { width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0; }
.dsk-bar    { height: 5px; width: 100%; border-radius: 3px; }
</style>